/**
 * CalendarMonthStats — Bandeau stats mensuelles sous le calendrier.
 *
 * Tier-gated : visible uniquement pour Basic+ (free voit un teaser).
 *
 * v1.12.233
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useI18n } from '../i18n';

// v1.12.276 — i18n key map for occasion labels coming from data
const OCC_LABEL_KEY = {
  chic: 'style_chic',
  casual: 'style_casual',
  minimaliste: 'style_minimal',
  minimal: 'style_minimal',
  soiree: 'style_evening',
  'soirée': 'style_evening',
  evening: 'style_evening',
  bureau: 'style_office',
  office: 'style_office',
  boheme: 'style_boho',
  'bohème': 'style_boho',
  boho: 'style_boho',
  sport: 'style_sport',
  elegant: 'style_elegant',
  'élégant': 'style_elegant',
};
function localizeOccasion(name, t) {
  if (!name) return '';
  const key = OCC_LABEL_KEY[String(name).toLowerCase()];
  return key ? t(key) : name;
}

export default function CalendarMonthStats({ stats, tier = 'free', monthLabel }) {
  const { t } = useI18n();
  const isPaid = tier !== 'free';

  if (!isPaid) {
    // Teaser pour free
    return (
      <View style={s.teaser}>
        <Text style={s.teaserIcon}>📊</Text>
        <View style={{ flex: 1 }}>
          <Text style={s.teaserTitle}>{t('stats_of_month')}</Text>
          <Text style={s.teaserDesc}>
            {t('stats_teaser_upgrade')}
          </Text>
        </View>
      </View>
    );
  }

  if (!stats) return null;

  const {
    days_tracked = 0, days_total = 0, coverage_percentage = 0,
    avg_rating, top_occasions = [], top_items_worn = [], insights = [],
  } = stats;

  const headerTitle = monthLabel
    ? t('stats_for_month').replace('{month}', monthLabel)
    : t('stats_for_month_default');
  const headerSub = t('days_tracked_total')
    .replace('{tracked}', String(days_tracked))
    .replace('{total}', String(days_total))
    .replace('{percent}', String(coverage_percentage));

  return (
    <View style={s.wrap}>
      {/* Header coverage */}
      <View style={s.header}>
        <Text style={s.headerTitle}>{headerTitle}</Text>
        <Text style={s.headerSub}>{headerSub}</Text>
      </View>

      {/* Progress bar */}
      <View style={s.bar}>
        <View style={[s.barFill, { width: `${Math.min(100, coverage_percentage)}%` }]} />
      </View>

      {/* KPIs row */}
      <View style={s.kpiRow}>
        <View style={s.kpi}>
          <Text style={s.kpiValue}>{days_tracked}</Text>
          <Text style={s.kpiLabel}>{t('days_tracked_label')}</Text>
        </View>
        <View style={s.kpiDivider} />
        <View style={s.kpi}>
          <Text style={s.kpiValue}>{avg_rating != null ? `${avg_rating}/5` : '—'}</Text>
          <Text style={s.kpiLabel}>{t('average_rating')}</Text>
        </View>
        <View style={s.kpiDivider} />
        <View style={s.kpi}>
          <Text style={s.kpiValue}>{top_occasions[0]?.emoji || '—'}</Text>
          <Text style={s.kpiLabel} numberOfLines={1}>
            {top_occasions[0]?.name ? localizeOccasion(top_occasions[0].name, t) : t('top_occasion_label')}
          </Text>
        </View>
      </View>

      {/* Top occasions */}
      {top_occasions.length > 0 && (
        <View style={s.section}>
          <Text style={s.sectionLabel}>{t('favorite_occasions')}</Text>
          {top_occasions.map((o, i) => (
            <View key={i} style={s.occRow}>
              <Text style={s.occEmoji}>{o.emoji}</Text>
              <Text style={s.occName}>{localizeOccasion(o.name, t)}</Text>
              <View style={s.occBarBg}>
                <View style={[s.occBarFill, { width: `${o.percentage}%` }]} />
              </View>
              <Text style={s.occCount}>{o.count}</Text>
            </View>
          ))}
        </View>
      )}

      {/* Top items */}
      {top_items_worn.length > 0 && (
        <View style={s.section}>
          <Text style={s.sectionLabel}>{t('most_worn_pieces')}</Text>
          {top_items_worn.slice(0, 3).map((it, i) => (
            <View key={i} style={s.itemRow}>
              <Text style={s.itemRank}>#{i + 1}</Text>
              <Text style={s.itemName} numberOfLines={1}>{it.name}</Text>
              <Text style={s.itemCount}>{it.times_worn}×</Text>
            </View>
          ))}
        </View>
      )}

      {/* Insights */}
      {insights.length > 0 && (
        <View style={s.insightsBox}>
          {insights.slice(0, 3).map((ins, i) => (
            <Text key={i} style={s.insight}>{ins}</Text>
          ))}
        </View>
      )}
    </View>
  );
}

const s = StyleSheet.create({
  wrap: {
    margin: 12,
    padding: 14,
    backgroundColor: '#FFFFFF',
    borderWidth: 1, borderColor: '#EAE6DF',
    borderRadius: 14,
    gap: 10,
  },
  header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'baseline' },
  headerTitle: { fontSize: 14, fontWeight: '700', color: '#1A1814' },
  headerSub:   { fontSize: 11, color: '#7A7268' },
  bar:     { height: 6, backgroundColor: '#F0EDE6', borderRadius: 3, overflow: 'hidden' },
  barFill: { height: 6, backgroundColor: '#D4AF72', borderRadius: 3 },

  kpiRow: { flexDirection: 'row', alignItems: 'center', paddingVertical: 8 },
  kpi:    { flex: 1, alignItems: 'center', gap: 2 },
  kpiValue: { fontSize: 18, fontWeight: '700', color: '#1A1814' },
  kpiLabel: { fontSize: 10, color: '#7A7268', letterSpacing: 0.4, textTransform: 'uppercase' },
  kpiDivider: { width: 1, height: 28, backgroundColor: '#F0EDE6' },

  section: { marginTop: 4 },
  sectionLabel: { fontSize: 10, fontWeight: '700', color: '#7A7268', letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 },

  occRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 4 },
  occEmoji: { fontSize: 14 },
  occName:  { fontSize: 12, color: '#1A1814', minWidth: 70, textTransform: 'capitalize' },
  occBarBg: { flex: 1, height: 4, backgroundColor: '#F0EDE6', borderRadius: 2, overflow: 'hidden' },
  occBarFill: { height: 4, backgroundColor: '#D4AF72', borderRadius: 2 },
  occCount: { fontSize: 11, color: '#7A7268', minWidth: 18, textAlign: 'right' },

  itemRow: { flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 3 },
  itemRank: { fontSize: 10, fontWeight: '700', color: '#D4AF72', width: 22 },
  itemName: { flex: 1, fontSize: 12, color: '#1A1814' },
  itemCount: { fontSize: 11, color: '#7A7268', fontWeight: '600' },

  insightsBox: {
    marginTop: 6,
    padding: 10,
    backgroundColor: '#FBF7EE',
    borderRadius: 10,
    gap: 4,
  },
  insight: { fontSize: 11, color: '#1A1814', lineHeight: 16 },

  teaser: {
    margin: 12, padding: 14,
    flexDirection: 'row', alignItems: 'center', gap: 12,
    backgroundColor: '#FBF7EE',
    borderWidth: 1, borderColor: 'rgba(212,175,114,0.35)',
    borderRadius: 14,
  },
  teaserIcon:  { fontSize: 28 },
  teaserTitle: { fontSize: 13, fontWeight: '700', color: '#1A1814', marginBottom: 2 },
  teaserDesc:  { fontSize: 11, color: '#5F584F', lineHeight: 16 },
});
